import {
  Form,
  Input,
  InputNumber,
  Select,
  Card,
  Typography,
  Divider,
  Upload,
  message,
  Button,
  Tag,
  Space,
  Switch,
  Spin,
} from 'antd';
import {
  LoadingOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PictureOutlined,
  FileGifOutlined,
  ApartmentOutlined,
  BookOutlined,
} from '@ant-design/icons';
import {useState, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {
  StoryCategory,
  StoryOwnerType,
  StoryLabel,
  MediaType,
  Media,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/story/graphql/story.interface';
import {storyStorageRepo} from '../StoryStorageRepository';
import {LevelsService} from '../../Level/LevelsService';
import {LevelUI} from '../../Level/LevelTypes';
import {GradesService} from '../../Grade/GradesService';
import {GradeUI} from '../../Grade/GradeTypes';

const levelsService = new LevelsService();
const gradesService = new GradesService();

const isS3Url = (url: string): boolean => {
  return /^https?:\/\/(.+\.)?s3\.[a-z0-9-]+\.amazonaws\.com\//.test(url);
};

const getMediaTypeFromExtension = (filename: string): MediaType => {
  const ext = filename.toLowerCase().split('.').pop() || '';

  const videoExtensions = ['mp4', 'webm', 'mov', 'avi', 'mkv', 'm4v'];
  const animationExtensions = ['gif'];
  const imageExtensions = ['jpg', 'jpeg', 'png', 'webp', 'bmp', 'svg'];

  if (videoExtensions.includes(ext)) return MediaType.VIDEO;
  if (animationExtensions.includes(ext)) return MediaType.ANIMATION;
  if (imageExtensions.includes(ext)) return MediaType.PHOTO;

  return MediaType.PHOTO; // Default to PHOTO
};

const getMediaTypeFromUrl = (url: string): MediaType => {
  const urlWithoutQuery = url.split('?')[0];
  return getMediaTypeFromExtension(urlWithoutQuery);
};

const getMediaTypeColor = (type: MediaType): string => {
  switch (type) {
    case MediaType.VIDEO:
      return 'blue';
    case MediaType.ANIMATION:
      return 'purple';
    case MediaType.PHOTO:
      return 'green';
    case MediaType.AUDIO:
      return 'orange';
    default:
      return 'default';
  }
};

const getMediaTypeIcon = (type: MediaType) => {
  switch (type) {
    case MediaType.VIDEO:
      return <PlayCircleOutlined />;
    case MediaType.ANIMATION:
      return <FileGifOutlined />;
    case MediaType.PHOTO:
      return <PictureOutlined />;
    default:
      return <PictureOutlined />;
  }
};

const {Title, Text} = Typography;
const {Option} = Select;

interface MetadataStepProps {
  storyId?: string;
}

export const MetadataStep = ({storyId: propStoryId}: MetadataStepProps) => {
  const {storyId: paramStoryId} = useParams<{storyId: string}>();
  const storyId = propStoryId || paramStoryId;
  const form = Form.useFormInstance();
  const [loading, setLoading] = useState(false);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);
  const coverMedia = Form.useWatch('coverMedia', form) as Media | undefined;
  const [levels, setLevels] = useState<LevelUI[]>([]);
  const [grades, setGrades] = useState<GradeUI[]>([]);
  const [levelsLoading, setLevelsLoading] = useState(false);
  const [gradesLoading, setGradesLoading] = useState(false);

  const [resolvedMediaUrl, setResolvedMediaUrl] = useState<string>();
  const [resolvedThumbnailUrl, setResolvedThumbnailUrl] = useState<string>();

  useEffect(() => {
    const fetchLevels = async () => {
      setLevelsLoading(true);
      try {
        const data = await levelsService.getLevels();
        setLevels(data || []);
      } catch (err) {
        console.error('Error loading levels:', err);
      } finally {
        setLevelsLoading(false);
      }
    };
    const fetchGrades = async () => {
      setGradesLoading(true);
      try {
        const data = await gradesService.getGrades();
        setGrades(data || []);
      } catch (err) {
        console.error('Error loading grades:', err);
      } finally {
        setGradesLoading(false);
      }
    };
    fetchLevels();
    fetchGrades();
  }, []);

  useEffect(() => {
    const resolve = async () => {
      const mediaUrl = coverMedia?.mediaUrl;
      if (!mediaUrl) {
        setResolvedMediaUrl(undefined);
        return;
      }

      // If it's an S3 URL, resolve/sign it
      if (isS3Url(mediaUrl)) {
        try {
          const signedUrl = await storyStorageRepo.resolveUrl(mediaUrl);
          setResolvedMediaUrl(signedUrl);
        } catch (error) {
          console.error('Error resolving S3 media:', error);
          setResolvedMediaUrl(mediaUrl);
        }
      } else {
        setResolvedMediaUrl(mediaUrl);
      }
    };
    resolve();
  }, [coverMedia?.mediaUrl]);

  useEffect(() => {
    const resolve = async () => {
      const thumbnailUrl = coverMedia?.thumbnailUrl;
      if (!thumbnailUrl) {
        setResolvedThumbnailUrl(undefined);
        return;
      }

      if (isS3Url(thumbnailUrl)) {
        try {
          const signedUrl = await storyStorageRepo.resolveUrl(thumbnailUrl);
          setResolvedThumbnailUrl(signedUrl);
        } catch (error) {
          console.error('Error resolving S3 thumbnail:', error);
          setResolvedThumbnailUrl(thumbnailUrl);
        }
      } else {
        setResolvedThumbnailUrl(thumbnailUrl);
      }
    };
    resolve();
  }, [coverMedia?.thumbnailUrl]);

  const handleUpload = async (options: any) => {
    const {file, onSuccess, onError} = options;
    if (!storyId) {
      message.error('Story ID is required for uploading');
      if (onError) onError(new Error('Story ID is required'));
      return;
    }
    try {
      setLoading(true);
      const detectedType = getMediaTypeFromExtension(file.name);
      const uploadType = detectedType === MediaType.VIDEO ? 'video' : 'image';
      const path = await storyStorageRepo.uploadFile(file, storyId, uploadType);

      const currentMedia = form.getFieldValue('coverMedia') || {};
      form.setFieldsValue({
        coverMedia: {
          ...currentMedia,
          mediaUrl: path,
          type: detectedType,
        },
      });

      message.success(
        `Cover ${detectedType.toLowerCase()} uploaded successfully`,
      );
      if (onSuccess) onSuccess(path);
    } catch (error) {
      console.error('Upload error:', error);
      message.error('Failed to upload cover media');
      if (onError) onError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleThumbnailUpload = async (options: any) => {
    const {file, onSuccess, onError} = options;
    if (!storyId) {
      message.error('Story ID is required for uploading');
      if (onError) onError(new Error('Story ID is required'));
      return;
    }
    try {
      setThumbnailLoading(true);
      const path = await storyStorageRepo.uploadFile(file, storyId, 'image');

      const currentMedia = form.getFieldValue('coverMedia') || {};
      form.setFieldsValue({
        coverMedia: {
          ...currentMedia,
          thumbnailUrl: path,
        },
      });

      message.success('Thumbnail uploaded successfully');
      if (onSuccess) onSuccess(path);
    } catch (error) {
      console.error('Thumbnail upload error:', error);
      message.error('Failed to upload thumbnail');
      if (onError) onError(error);
    } finally {
      setThumbnailLoading(false);
    }
  };

  const removeMedia = () => {
    form.setFieldsValue({coverMedia: undefined});
  };

  const removeThumbnail = () => {
    const currentMedia = form.getFieldValue('coverMedia') || {};
    form.setFieldsValue({
      coverMedia: {
        ...currentMedia,
        thumbnailUrl: undefined,
      },
    });
  };

  return (
    <Card bordered={false} style={{background: 'transparent'}}>
      <div style={{marginBottom: '24px'}}>
        <Title level={4}>Story Information</Title>
        <Text type="secondary">Basic details about your story</Text>
      </div>

      <div style={{display: 'flex', gap: '24px'}}>
        <Form.Item
          name={['title', 'en']}
          label="Story Title (English)"
          rules={[{required: true, message: 'English title is required'}]}
          style={{flex: 1}}>
          <Input size="large" placeholder="Enter story title in English" />
        </Form.Item>
        <Form.Item
          name={['title', 'ar']}
          label="Story Title (Arabic)"
          rules={[{required: true, message: 'Arabic title is required'}]}
          style={{flex: 1}}>
          <Input
            size="large"
            placeholder="Enter story title in Arabic"
            style={{direction: 'rtl'}}
          />
        </Form.Item>
      </div>

      <div style={{display: 'flex', gap: '24px'}}>
        <Form.Item
          name={['subtitle', 'en']}
          label="Subtitle (English)"
          rules={[{required: true, message: 'English subtitle is required'}]}
          style={{flex: 1}}>
          <Input size="large" placeholder="Enter subtitle in English" />
        </Form.Item>

        <Form.Item
          name={['subtitle', 'ar']}
          label="Subtitle (Arabic)"
          rules={[{required: true, message: 'Arabic subtitle is required'}]}
          style={{flex: 1}}>
          <Input
            size="large"
            placeholder="Enter subtitle in Arabic"
            style={{direction: 'rtl'}}
          />
        </Form.Item>
      </div>

      <div style={{display: 'flex', gap: '24px'}}>
        <Form.Item
          name={['description', 'en']}
          label="Description (English)"
          rules={[{required: true, message: 'English description is required'}]}
          style={{flex: 1}}>
          <Input.TextArea rows={4} placeholder="Enter description in English" />
        </Form.Item>
        <Form.Item
          name={['description', 'ar']}
          label="Description (Arabic)"
          rules={[{required: true, message: 'Arabic description is required'}]}
          style={{flex: 1}}>
          <Input.TextArea
            rows={4}
            placeholder="Enter description in Arabic"
            style={{direction: 'rtl'}}
          />
        </Form.Item>
      </div>

      <div style={{display: 'flex', gap: '24px'}}>
        <Form.Item
          name={['summary', 'en']}
          label="Story Summary (English)"
          style={{flex: 1}}
          rules={[{required: true, message: 'Story summary is required'}]}>
          <Input.TextArea
            rows={3}
            placeholder="Enter a brief summary in English"
          />
        </Form.Item>
        <Form.Item
          name={['summary', 'ar']}
          label="Story Summary (Arabic)"
          style={{flex: 1}}
          rules={[{required: true, message: 'Story summary is required'}]}>
          <Input.TextArea
            rows={3}
            placeholder="Enter a brief summary in Arabic"
            style={{direction: 'rtl'}}
          />
        </Form.Item>
      </div>

      <Divider />

      <div style={{display: 'flex', gap: '24px', alignItems: 'flex-start'}}>
        <div style={{flex: 1}}>
          <Form.Item
            name="category"
            label="Category"
            rules={[{required: true}]}>
            <Select size="large">
              {Object.values(StoryCategory).map(cat => (
                <Option key={cat} value={cat}>
                  {cat}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="labels" label="Labels">
            <Select mode="multiple" size="large">
              {Object.values(StoryLabel).map(label => (
                <Option key={label} value={label}>
                  {label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="ownerType"
            label="Owner Type"
            initialValue={StoryOwnerType.PIKADO}>
            <Select size="large">
              <Option value={StoryOwnerType.PIKADO}>PikaDo</Option>
              <Option value={StoryOwnerType.CLIENT}>Client</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="storyOrder"
            label="Story Order"
            rules={[{required: true, message: 'Story order is required'}]}>
            <InputNumber
              size="large"
              placeholder="Enter display order"
              min={0}
              style={{width: '100%'}}
            />
          </Form.Item>

          <Form.Item
            name="isFreemium"
            label="Freemium"
            valuePropName="checked"
            initialValue={false}
            extra="Enable to make this story available as freemium content">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          {/* ── Level assignment ── */}
          <Form.Item
            name="levelId"
            label={
              <span>
                <ApartmentOutlined style={{marginRight: 6, color: '#6366f1'}} />
                Level
              </span>
            }
            rules={[
              ({getFieldValue}) => ({
                validator() {
                  if (getFieldValue('levelId') || getFieldValue('gradeId')) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('Please assign at least a Level or a Grade'),
                  );
                },
              }),
            ]}
            dependencies={['gradeId']}
            extra="Choose the difficulty level this story belongs to">
            <Select
              size="large"
              placeholder="Select a level"
              allowClear
              showSearch
              optionFilterProp="children"
              notFoundContent={
                levelsLoading ? <Spin size="small" /> : 'No levels found'
              }
              loading={levelsLoading}
              style={{borderRadius: '8px'}}>
              {levels.map(lvl => (
                <Option key={lvl._id} value={lvl._id}>
                  {lvl.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* ── Grade assignment ── */}
          <Form.Item
            name="gradeId"
            label={
              <span>
                <BookOutlined style={{marginRight: 6, color: '#a855f7'}} />
                Grade
              </span>
            }
            rules={[
              ({getFieldValue}) => ({
                validator() {
                  if (getFieldValue('levelId') || getFieldValue('gradeId')) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('Please assign at least a Level or a Grade'),
                  );
                },
              }),
            ]}
            dependencies={['levelId']}
            extra="Choose the school grade this story targets">
            <Select
              size="large"
              placeholder="Select a grade"
              allowClear
              showSearch
              optionFilterProp="children"
              notFoundContent={
                gradesLoading ? <Spin size="small" /> : 'No grades found'
              }
              loading={gradesLoading}
              style={{borderRadius: '8px'}}>
              {grades.map(grd => (
                <Option key={grd._id} value={grd._id}>
                  {grd.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div style={{flex: 1}}>
          <Form.Item
            name={['coverMedia', 'mediaUrl']}
            label="Cover Media URL"
            extra="Paste a URL or upload an image, video, or GIF file"
            rules={[{required: true, message: 'Cover media is required'}]}>
            <Input
              size="large"
              placeholder="https://example.com/media.jpg"
              onChange={e => {
                if (e.target.value) {
                  const detectedType = getMediaTypeFromUrl(e.target.value);
                  const currentMedia = form.getFieldValue('coverMedia') || {};
                  form.setFieldsValue({
                    coverMedia: {
                      ...currentMedia,
                      mediaUrl: e.target.value,
                      type: detectedType,
                    },
                  });
                }
              }}
              suffix={
                <Upload
                  customRequest={handleUpload}
                  showUploadList={false}
                  accept="image/*,video/*,.gif">
                  <Button
                    type="text"
                    size="small"
                    icon={
                      loading ? <LoadingOutlined /> : <CloudUploadOutlined />
                    }
                    style={{
                      color: '#6366f1',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    Choose File
                  </Button>
                </Upload>
              }
            />
          </Form.Item>

          <Form.Item
            name={['coverMedia', 'type']}
            label="Media Type"
            rules={[{required: true, message: 'Media type is required'}]}>
            <Select size="large" disabled>
              <Option value={MediaType.PHOTO}>
                <Space>
                  <PictureOutlined /> Photo
                </Space>
              </Option>
              <Option value={MediaType.VIDEO}>
                <Space>
                  <PlayCircleOutlined /> Video
                </Space>
              </Option>
              <Option value={MediaType.ANIMATION}>
                <Space>
                  <FileGifOutlined /> Animation (GIF)
                </Space>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            name={['coverMedia', 'thumbnailUrl']}
            label="Thumbnail URL"
            extra={
              coverMedia?.type === MediaType.VIDEO
                ? 'Recommended for videos'
                : 'Optional'
            }>
            <Input
              size="large"
              placeholder="https://example.com/thumbnail.jpg"
              suffix={
                <Upload
                  customRequest={handleThumbnailUpload}
                  showUploadList={false}
                  accept="image/*">
                  <Button
                    type="text"
                    size="small"
                    icon={
                      thumbnailLoading ? (
                        <LoadingOutlined />
                      ) : (
                        <CloudUploadOutlined />
                      )
                    }
                    style={{
                      color: '#6366f1',
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    Upload
                  </Button>
                </Upload>
              }
            />
          </Form.Item>

          {coverMedia?.mediaUrl && (
            <div style={{marginTop: '16px'}}>
              <div
                style={{
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                <Text strong>Preview</Text>
                <Tag
                  color={getMediaTypeColor(coverMedia.type)}
                  icon={getMediaTypeIcon(coverMedia.type)}>
                  {coverMedia.type}
                </Tag>
              </div>

              <div style={{display: 'flex', gap: '16px'}}>
                {/* Main Media Preview */}
                <div
                  style={{
                    position: 'relative',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid #eee',
                    flex: 1,
                  }}>
                  {coverMedia.type === MediaType.VIDEO ? (
                    <video
                      src={resolvedMediaUrl}
                      controls
                      style={{
                        width: '100%',
                        maxHeight: '200px',
                        objectFit: 'contain',
                        background: '#000',
                      }}
                    />
                  ) : (
                    <img
                      src={resolvedMediaUrl}
                      alt="Cover"
                      style={{
                        width: '100%',
                        maxHeight: '200px',
                        objectFit: 'cover',
                      }}
                    />
                  )}
                  <Button
                    type="primary"
                    danger
                    size="small"
                    shape="circle"
                    icon={<DeleteOutlined />}
                    onClick={removeMedia}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                    }}
                  />
                </div>

                {/* Thumbnail Preview */}
                {coverMedia.thumbnailUrl && (
                  <div
                    style={{
                      position: 'relative',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: '1px solid #eee',
                      width: '120px',
                      flexShrink: 0,
                    }}>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: '10px',
                        position: 'absolute',
                        top: '4px',
                        left: '8px',
                        background: 'rgba(255,255,255,0.8)',
                        padding: '2px 6px',
                        borderRadius: '4px',
                      }}>
                      Thumbnail
                    </Text>
                    <img
                      src={resolvedThumbnailUrl}
                      alt="Thumbnail"
                      style={{
                        width: '100%',
                        height: '100px',
                        objectFit: 'cover',
                      }}
                    />
                    <Button
                      type="primary"
                      danger
                      size="small"
                      shape="circle"
                      icon={<DeleteOutlined />}
                      onClick={removeThumbnail}
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        width: '20px',
                        height: '20px',
                        minWidth: '20px',
                        fontSize: '10px',
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
